// Embed height reporting — `/d/<id>?embed=1` telling its host how tall it is.
//
// The embed's natural height depends on its WIDTH, because the transform and
// range toolbars wrap: ~664 px at 580 px wide and up, 744 px at 420, 784 px at
// 330. A host that picks one number is therefore wrong at some widths — too
// small and the iframe scrolls internally, too large and it wastes space. Only
// the content can know, and a cross-origin parent cannot measure it, so we say.
//
//   iframe -> parent, on mount and whenever the height changes
//   parent.postMessage({ type: "dataseries:height", height: 664 }, "*")
//
// This is a progressive enhancement on top of EMBED_HEIGHT (the published
// default in the copy-paste snippet, /oembed and llms.txt): a host that ignores
// the message keeps the static height and is merely approximate. See
// lib/theme.ts for the other half of this contract.

/** iframe -> parent, with the current content height in CSS pixels. */
export const HEIGHT_MESSAGE = "dataseries:height";

// What we WOULD measure but must not: document.documentElement.scrollHeight is
// never less than the viewport (#app is min-height: 100vh), so it reports the
// iframe's own height back to the iframe. A host that applied it would grow the
// frame, which would grow the reading, which would grow the frame. Measuring the
// content element instead depends only on the width, so it cannot feed back.
let observer: ResizeObserver | null = null;
let lastSent = 0;

/** Round up — a fractional layout height must not round down into a scrollbar. */
function heightOf(el: Element): number {
  return Math.ceil(el.getBoundingClientRect().height);
}

function send(height: number): void {
  // Only on a real change: the observer also fires for width-only resizes, and
  // a host that re-sizes its frame in response must not be talked into a loop.
  if (height <= 0 || height === lastSent) return;
  lastSent = height;
  window.parent.postMessage({ type: HEIGHT_MESSAGE, height }, "*");
}

/**
 * Report `el`'s height to the parent, now and on every change. Safe to call
 * again after a re-render — the previous observer is dropped, since renderDetail
 * replaces the element and a detached one silently stops reporting.
 * No-op outside an iframe.
 */
export function watchEmbedHeight(el: Element | null): void {
  if (!el || !window.parent || window.parent === window) return;
  observer?.disconnect();
  if (typeof ResizeObserver !== "function") { send(heightOf(el)); return; }
  // The first callback fires on observe(), so the initial height needs no
  // separate send. Later ones cover the async chart draw and the web font
  // landing, both of which change the layout after first paint.
  observer = new ResizeObserver(() => send(heightOf(el)));
  observer.observe(el);
}
